import { useState, useMemo } from 'react';
import { Slot } from '../hooks/useScheduling';
import { Calendar, Clock } from 'lucide-react';
import { DateCalendar } from './DateCalendar';

interface SlotPickerProps {
  slots: Slot[];
  selectedSlot: Slot | null;
  onSelectSlot: (slot: Slot) => void;
  isLoading?: boolean;
}

interface SlotsByDay {
  [dateKey: string]: {
    displayDate: string;
    slots: Slot[];
    rawDate: Date;
  };
}

export function SlotPicker({ slots, selectedSlot, onSelectSlot, isLoading }: SlotPickerProps) {
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  // Agrupar slots por dia
  const slotsByDay: SlotsByDay = useMemo(() => {
    return slots.reduce((acc, slot) => {
      const date = new Date(slot.startTime);
      const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD format for key
      const displayDate = date.toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
      });

      if (!acc[dateKey]) {
        acc[dateKey] = {
          displayDate,
          slots: [],
          rawDate: date,
        };
      }

      acc[dateKey].slots.push(slot);

      return acc;
    }, {} as SlotsByDay);
  }, [slots]);

  // Extrair datas disponíveis (deve estar antes dos early returns)
  const availableDates = useMemo(() => {
    return Object.keys(slotsByDay).sort();
  }, [slotsByDay]);

  const selectedDaySlots = selectedDay ? slotsByDay[selectedDay]?.slots : [];

  const formatTime = (dateString: string): string => {
    return new Date(dateString).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isSlotSelected = (slot: Slot): boolean => {
    return selectedSlot?.startTime === slot.startTime;
  };

  const handleDaySelect = (dateKey: string) => {
    setSelectedDay(dateKey);
  };

  const handleSlotSelect = (slot: Slot) => {
    onSelectSlot(slot);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-3">
          <div className="h-6 bg-muted rounded w-32 mb-3"></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-muted rounded"></div>
            ))}
          </div>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-6 bg-muted rounded w-32 mb-3"></div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {[1, 2, 3, 4].map((j) => (
              <div key={j} className="h-12 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (slots.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
          <Calendar className="w-8 h-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Nenhum horário disponível</h3>
        <p className="text-muted-foreground">
          No momento não há horários disponíveis para agendamento.
          <br />
          Tente novamente mais tarde.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Seleção de Dia - Calendário */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-foreground">Selecione o dia</h3>
        </div>
        <DateCalendar
          availableDates={availableDates}
          selectedDate={selectedDay}
          onSelectDate={handleDaySelect}
        />
      </div>

      {/* Seleção de Horário */}
      {selectedDay && selectedDaySlots.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-foreground">Selecione o horário</h3>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
            {selectedDaySlots.map((slot, index) => {
              const isSelected = isSlotSelected(slot);
              return (
                <button
                  key={`${slot.startTime}-${index}`}
                  onClick={() => handleSlotSelect(slot)}
                  className={`
                    p-3 rounded-lg border-2 font-medium transition-all duration-200
                    hover:scale-105 active:scale-95
                    ${
                      isSelected
                        ? 'border-primary bg-primary/10 dark:bg-primary/20 text-primary shadow-md'
                        : 'border-border bg-card text-card-foreground hover:border-primary/50 hover:bg-primary/5'
                    }
                  `}
                >
                  {formatTime(slot.startTime)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
